import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Briefcase,
  Search,
  ArrowRight,
  Calendar,
  User,
  Sparkles,
  CheckCircle2,
  Layers,
  Kanban,
  FileText,
} from "lucide-react";
import { Opportunity, OpportunityStage, UserProfile } from "../../types";
import { subscribeToOpportunities } from "../../services/opportunitiesService";
import { setActiveOpportunity } from "../../services/activeOpportunityService";

export interface WorkspacePickerViewProps {
  user?: UserProfile | null;
}

const ACTIVE_STAGES: OpportunityStage[] = ["reviewing", "go", "drafting", "in_review", "approved"];

const STAGE_LABELS: Record<OpportunityStage, string> = {
  reviewing: "Reviewing / Intake",
  no_go: "Declined (No-Go)",
  go: "Qualified (Go)",
  drafting: "Drafting",
  in_review: "In Review",
  approved: "Approved",
  submitted: "Submitted",
  won: "Won",
  lost: "Lost",
  archived: "Archived",
};

const STAGE_STYLES: Record<OpportunityStage, { bg: string; text: string; border: string }> = {
  reviewing: {
    bg: "bg-amber-50 dark:bg-amber-950/40",
    text: "text-amber-700 dark:text-amber-300",
    border: "border-amber-200 dark:border-amber-800/60",
  },
  go: {
    bg: "bg-teal-50 dark:bg-teal-950/40",
    text: "text-[#2ba5b5] dark:text-teal-300",
    border: "border-teal-200 dark:border-teal-800/60",
  },
  drafting: {
    bg: "bg-blue-50 dark:bg-blue-950/40",
    text: "text-blue-700 dark:text-blue-300",
    border: "border-blue-200 dark:border-blue-800/60",
  },
  in_review: {
    bg: "bg-purple-50 dark:bg-purple-950/40",
    text: "text-purple-700 dark:text-purple-300",
    border: "border-purple-200 dark:border-purple-800/60",
  },
  approved: {
    bg: "bg-emerald-50 dark:bg-emerald-950/40",
    text: "text-emerald-700 dark:text-emerald-300",
    border: "border-emerald-200 dark:border-emerald-800/60",
  },
  submitted: {
    bg: "bg-cyan-50 dark:bg-cyan-950/40",
    text: "text-cyan-700 dark:text-cyan-300",
    border: "border-cyan-200 dark:border-cyan-800/60",
  },
  won: {
    bg: "bg-emerald-100 dark:bg-emerald-900/50",
    text: "text-emerald-800 dark:text-emerald-200",
    border: "border-emerald-300 dark:border-emerald-700",
  },
  lost: {
    bg: "bg-rose-50 dark:bg-rose-950/40",
    text: "text-rose-700 dark:text-rose-300",
    border: "border-rose-200 dark:border-rose-800/60",
  },
  no_go: {
    bg: "bg-slate-100 dark:bg-slate-800/60",
    text: "text-slate-700 dark:text-slate-300",
    border: "border-slate-200 dark:border-slate-700",
  },
  archived: {
    bg: "bg-slate-100 dark:bg-slate-800/40",
    text: "text-slate-500 dark:text-slate-400",
    border: "border-slate-200 dark:border-slate-700/50",
  },
};

export const WorkspacePickerView: React.FC<WorkspacePickerViewProps> = () => {
  const navigate = useNavigate();
  const [opportunities, setOpportunities] = useState<Opportunity[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [stageFilter, setStageFilter] = useState<string>("all");

  useEffect(() => {
    const unsubscribe = subscribeToOpportunities(
      (items) => {
        setOpportunities(items);
        setLoading(false);
      },
      (err) => {
        console.error("Failed to fetch opportunities for workspace picker:", err);
        setLoading(false);
      },
    );

    return () => unsubscribe();
  }, []);

  const activeOpportunities = useMemo(() => {
    return opportunities.filter((opp) => !opp.isArchived && ACTIVE_STAGES.includes(opp.stage));
  }, [opportunities]);

  const filteredOpportunities = useMemo(() => {
    return activeOpportunities.filter((opp) => {
      const matchesSearch =
        searchQuery.trim() === "" ||
        opp.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        opp.client.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (opp.owner && opp.owner.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (opp.services &&
          opp.services.some((s) => s.toLowerCase().includes(searchQuery.toLowerCase())));

      const matchesStage = stageFilter === "all" || opp.stage === stageFilter;

      return matchesSearch && matchesStage;
    });
  }, [activeOpportunities, searchQuery, stageFilter]);

  const handleOpenWorkspace = (opp: Opportunity) => {
    setActiveOpportunity({
      id: opp.id,
      title: opp.title,
      client: opp.client,
      stage: opp.stage,
    });
    navigate(`/opportunities/${opp.id}`);
  };

  const getDaysRemaining = (dueDateStr: string): { label: string; urgent: boolean } => {
    if (!dueDateStr) return { label: "No date", urgent: false };
    const due = new Date(dueDateStr);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const diffTime = due.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays < 0) {
      return { label: `${Math.abs(diffDays)}d overdue`, urgent: true };
    }
    if (diffDays === 0) {
      return { label: "Due today", urgent: true };
    }
    if (diffDays <= 3) {
      return { label: `${diffDays}d left`, urgent: true };
    }
    return { label: `${diffDays}d left`, urgent: false };
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
      {/* Top Banner / Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pb-4 border-b border-slate-200 dark:border-slate-800">
        <div>
          <div className="flex items-center gap-2">
            <div className="p-2 rounded-lg bg-teal-50 dark:bg-teal-950/40 text-[#2ba5b5] border border-teal-200 dark:border-teal-800/60">
              <Briefcase className="w-5 h-5" />
            </div>
            <h1 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-white">
              Proposal Workspace
            </h1>
          </div>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            Select an in-flight proposal to jump directly into requirements analysis, drafting,
            compliance checks, and AI coaching.
          </p>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate("/")}
            className="inline-flex items-center gap-2 px-3.5 py-2 text-sm font-medium rounded-lg text-slate-700 dark:text-slate-200 bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700/80 border border-slate-200 dark:border-slate-700 shadow-xs transition-colors cursor-pointer"
          >
            <Kanban className="w-4 h-4 text-slate-500" />
            <span>View Full Pipeline</span>
          </button>
        </div>
      </div>

      {/* Filter and Search Controls */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            placeholder="Search proposals by title, client, owner..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-4 py-2 text-sm rounded-lg bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white placeholder-slate-400 focus:outline-hidden focus:ring-2 focus:ring-[#2ba5b5] focus:border-transparent transition-all shadow-xs"
          />
        </div>

        <div className="flex items-center gap-2 overflow-x-auto pb-1 sm:pb-0">
          <button
            onClick={() => setStageFilter("all")}
            className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors cursor-pointer ${
              stageFilter === "all"
                ? "bg-[#2ba5b5] text-white shadow-xs"
                : "bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700"
            }`}
          >
            All Active ({activeOpportunities.length})
          </button>
          {ACTIVE_STAGES.map((stg) => {
            const count = activeOpportunities.filter((o) => o.stage === stg).length;
            if (count === 0 && stageFilter !== stg) return null;
            return (
              <button
                key={stg}
                onClick={() => setStageFilter(stg)}
                className={`px-3 py-1.5 text-xs font-medium rounded-md whitespace-nowrap transition-colors cursor-pointer ${
                  stageFilter === stg
                    ? "bg-[#2ba5b5] text-white shadow-xs"
                    : "bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700"
                }`}
              >
                {STAGE_LABELS[stg]} ({count})
              </button>
            );
          })}
        </div>
      </div>

      {/* Main Content Area */}
      {loading ? (
        <div className="flex flex-col items-center justify-center py-20">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#2ba5b5]" />
          <p className="mt-4 text-sm text-slate-500 dark:text-slate-400">
            Loading active workspaces...
          </p>
        </div>
      ) : filteredOpportunities.length === 0 ? (
        <div className="text-center py-16 px-4 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl shadow-xs">
          <div className="mx-auto w-12 h-12 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-slate-400 mb-4">
            <Layers className="w-6 h-6" />
          </div>
          <h3 className="text-base font-semibold text-slate-900 dark:text-white">
            No active proposals found
          </h3>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400 max-w-md mx-auto">
            {searchQuery || stageFilter !== "all"
              ? "No proposals match your current filter criteria. Try adjusting your search query or stage filter."
              : "There are currently no proposals in the active drafting or review stages."}
          </p>
          <div className="mt-6 flex items-center justify-center gap-3">
            {(searchQuery || stageFilter !== "all") && (
              <button
                onClick={() => {
                  setSearchQuery("");
                  setStageFilter("all");
                }}
                className="px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 bg-slate-100 dark:bg-slate-800 rounded-lg hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors cursor-pointer"
              >
                Clear Filters
              </button>
            )}
            <button
              onClick={() => navigate("/")}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-[#2ba5b5] rounded-lg hover:bg-[#2594a3] transition-colors cursor-pointer"
            >
              <Kanban className="w-4 h-4" />
              <span>Go to Pipeline</span>
            </button>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {filteredOpportunities.map((opp) => {
            const daysRemaining = getDaysRemaining(opp.dueDate);
            const style = STAGE_STYLES[opp.stage] || STAGE_STYLES.reviewing;
            const reqSummary = opp.requirementsSummary;
            const fitScore = opp.assessmentSummary?.overallFitScore;

            return (
              <motion.div
                key={opp.id}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.15 }}
                className="flex flex-col justify-between bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 hover:border-[#2ba5b5]/50 dark:hover:border-[#2ba5b5]/50 rounded-xl p-5 shadow-xs hover:shadow-md transition-all group"
              >
                <div className="space-y-3">
                  {/* Client & Stage Badge */}
                  <div className="flex items-start justify-between gap-2">
                    <span className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 truncate">
                      {opp.client}
                    </span>
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${style.bg} ${style.text} ${style.border}`}
                    >
                      {STAGE_LABELS[opp.stage]}
                    </span>
                  </div>

                  {/* RFP Title */}
                  <h3 className="text-base font-semibold text-slate-900 dark:text-white line-clamp-2 group-hover:text-[#2ba5b5] transition-colors">
                    {opp.title}
                  </h3>

                  {/* Metadata: Due Date & Owner */}
                  <div className="space-y-1.5 pt-1 text-xs text-slate-500 dark:text-slate-400">
                    <div className="flex items-center gap-2">
                      <Calendar className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                      <span>Due: {opp.dueDate || "Not specified"}</span>
                      {opp.dueDate && (
                        <span
                          className={`ml-auto font-medium px-1.5 py-0.5 rounded text-[11px] ${
                            daysRemaining.urgent
                              ? "bg-rose-50 text-rose-700 dark:bg-rose-950/50 dark:text-rose-300"
                              : "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300"
                          }`}
                        >
                          {daysRemaining.label}
                        </span>
                      )}
                    </div>

                    {opp.owner && (
                      <div className="flex items-center gap-2">
                        <User className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                        <span className="truncate">Lead: {opp.owner}</span>
                      </div>
                    )}
                  </div>

                  {/* Progress & Quality Metrics */}
                  <div className="pt-2 border-t border-slate-100 dark:border-slate-800/80 flex items-center justify-between text-xs text-slate-500 dark:text-slate-400">
                    {reqSummary ? (
                      <div
                        className="flex items-center gap-1.5"
                        title="Requirements parsed & confirmed"
                      >
                        <FileText className="w-3.5 h-3.5 text-[#2ba5b5]" />
                        <span>
                          {reqSummary.confirmed}/{reqSummary.total} Reqs
                        </span>
                      </div>
                    ) : (
                      <span className="text-slate-400 italic">No reqs extracted</span>
                    )}

                    {typeof fitScore === "number" ? (
                      <div
                        className="flex items-center gap-1.5 font-medium"
                        title="AI Strategic Fit Score"
                      >
                        <Sparkles className="w-3.5 h-3.5 text-amber-500" />
                        <span>Fit: {fitScore}/100</span>
                      </div>
                    ) : opp.stage === "go" ? (
                      <div className="flex items-center gap-1 text-teal-600 dark:text-teal-400">
                        <CheckCircle2 className="w-3.5 h-3.5" />
                        <span>Qualified</span>
                      </div>
                    ) : null}
                  </div>
                </div>

                {/* Bottom Action Button */}
                <div className="pt-4 mt-4 border-t border-slate-100 dark:border-slate-800">
                  <button
                    onClick={() => handleOpenWorkspace(opp)}
                    className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium rounded-lg text-white bg-[#2ba5b5] hover:bg-[#2594a3] active:scale-[0.99] transition-all cursor-pointer shadow-xs"
                  >
                    <span>Open in Workspace</span>
                    <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}
    </div>
  );
};
